import * as amplitude from "@amplitude/analytics-browser";
import { DEFAULT_CSS_SELECTOR_ALLOWLIST } from "@amplitude/analytics-core";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import type {
	ContractSuretyAccountUnderwritingModelSize,
	SuretyAccountId,
	SuretyBondId,
	SuretyQuoteId,
	UserType,
} from "@inrev/common";
import type { AuthenticatedUser } from "./providers/AuthenticatedUserProvider";

// Joined tokens are bounded at five so "John Doe.pdf" masks whole without a
// filename mid-sentence swallowing the sentence.
const UPLOADED_FILE_NAME_REGEX =
	/[^\s/\\]+(?:[ _-][^\s/\\]+){0,4}\.(?:pdf|docx?|xlsx?|xlsm|csv|txt|rtf|zip|png|jpe?g|gif|heic|tiff?)\b/gi;

export function initAmplitude(apiKey: string) {
	amplitude.add(
		sessionReplayPlugin({
			sampleRate: 1,
			privacyConfig: {
				defaultMaskLevel: "medium",
				maskSelector: ["[data-amp-mask]", ".amp-mask"],
				blockSelector: ["[data-amp-block]"],
			},
		}),
	);
	amplitude.init(apiKey, {
		autocapture: {
			// 2.45+ would also attach campaign params to every event. Keep them
			// as user properties only, matching the pre-upgrade payload shape.
			attribution: { trackingMethod: ["userProperty"] },
			pageViews: true,
			sessions: true,
			webVitals: true,
			frustrationInteractions: true,
			formInteractions: true,
			elementInteractions: {
				cssSelectorAllowlist: [...DEFAULT_CSS_SELECTOR_ALLOWLIST, "[data-amp-track-id]"],
				maskTextRegex: [UPLOADED_FILE_NAME_REGEX],
			},
			fileDownloads: true,
		},
		transport: "beacon",
		// Skip analytics remote config: settings live in this file. Avoids N sequential
		// sr-client-cfg fetches per pageload (PROD-PORTAL-B0). Session Replay still fetches its own.
		remoteConfig: { fetchRemoteConfig: false },
	});
}

export function identifyAmplitudeUser(user: AuthenticatedUser) {
	const identifyEvent = new amplitude.Identify();
	identifyEvent.set("userType", user.type);
	identifyEvent.set("email", user.email);
	identifyEvent.set("name", `${user.firstName} ${user.lastName}`);

	amplitude.setUserId(user.id);
	amplitude.identify(identifyEvent);

	if (user.type === "agent") {
		amplitude.setGroup("agency", user.agencyId);
	}
}

export type SearchSurface = "modal" | "dashboard";
export type SearchOpenedVia = "hotkey" | "header_button";
export type SearchResultSource = "search" | "recentlyViewed";
export type SearchRelationType = "account" | "bondRequest" | "bond";

export function trackSearchOpened(userType: UserType, openedVia: SearchOpenedVia) {
	amplitude.track("Search Opened", { userType, surface: "modal", openedVia });
}

export function trackSearchPerformed(
	userType: UserType,
	searchTerm: string,
	surface: SearchSurface,
) {
	amplitude.track("Search Performed", { userType, searchTerm, surface });
}

export function trackSearchResultClicked(
	relationType: SearchRelationType,
	entityId: SuretyAccountId | SuretyQuoteId | SuretyBondId,
	surface: SearchSurface,
	resultSource: SearchResultSource,
) {
	amplitude.track("Search Result Clicked", { relationType, entityId, surface, resultSource });
}

export function trackSearchAbandoned(
	userType: UserType,
	searchTerm: string,
	surface: SearchSurface,
) {
	amplitude.track("Search Abandoned", { userType, searchTerm, surface });
}

export function trackTableFilterChanged(
	tableName: string,
	columnId: string,
	filterValues: string[],
) {
	amplitude.track("Table Filter Changed", { tableName, columnId, filterValues });
}

export type BondRequestFlowEntry = "page" | "modal" | "quick";

/**
 * Which form the user was on when the event fired. Differs from `entry` once an
 * admin escalates a quick submit to the full modal, which is the only way to
 * tell those two admin paths apart.
 */
export type BondRequestFlowSurface = BondRequestFlowEntry | "full";
export type BondRequestFlowOutcome = "approved" | "review" | "declined";

type BondRequestFlowContext = {
	accountId: SuretyAccountId;
	entry: BondRequestFlowEntry;
	surface: BondRequestFlowSurface;
};

type BondRequestFlowCompletedPayload = BondRequestFlowContext & {
	bondRequestId: SuretyQuoteId;
};

type BondRequestOutcomeShownPayload = BondRequestFlowCompletedPayload & {
	outcome: BondRequestFlowOutcome;
};

export type BondRequestWaitResolution = "waited" | "backgrounded";

type BondRequestWaitResolvedPayload = BondRequestFlowCompletedPayload & {
	resolution: BondRequestWaitResolution;
	/** Time on the waiting step, in whole seconds. */
	waitDurationSeconds: number;
};

type BondRequestFlowFailedPayload = BondRequestFlowContext & {
	errorName: string;
};

export function trackBondRequestFlowEntered(payload: BondRequestFlowContext) {
	amplitude.track("Bond Request Flow Entered", payload);
}

/**
 * The conversion event: a bond request now exists. Fires when the request is
 * created, not when the underwriting outcome is rendered -- an agent who closes
 * the modal while underwriting runs has still converted.
 */
export function trackBondRequestFlowCompleted(payload: BondRequestFlowCompletedPayload) {
	amplitude.track("Bond Request Flow Completed", payload);
}

/**
 * Fires when the user is actually shown the result, so `outcome` is the screen
 * they landed on rather than the underwriting status behind it. Always preceded
 * by "Bond Request Flow Completed". Only the extraction modal waits out
 * underwriting in-flow, so only there is the gap between the two meaningful.
 */
export function trackBondRequestOutcomeShown(payload: BondRequestOutcomeShownPayload) {
	amplitude.track("Bond Request Outcome Shown", payload);
}

export function trackBondRequestFlowAbandoned(payload: BondRequestFlowContext) {
	amplitude.track("Bond Request Flow Abandoned", payload);
}

/**
 * How the user got out of the post-submit wait, not whether they converted --
 * the request already exists by then, so neither resolution is a drop-off.
 * `waited` means they sat on the modal through extraction and underwriting and
 * were shown the outcome in-flow; `backgrounded` means they closed out and
 * picked the result up from the toast instead.
 *
 * `waitDurationSeconds` means different things per resolution and shouldn't be
 * averaged across both: on `waited` it's how long extraction took, on
 * `backgrounded` it's how long the user was willing to sit there first.
 */
export function trackBondRequestWaitResolved(payload: BondRequestWaitResolvedPayload) {
	amplitude.track("Bond Request Wait Resolved", payload);
}

/**
 * A failed submit leaves the user on the form to retry, so this
 * can fire more than once per flow and can be followed by a completion.
 */
export function trackBondRequestFlowFailed(payload: BondRequestFlowFailedPayload) {
	amplitude.track("Bond Request Flow Failed", payload);
}

type AccountSubmissionFlowVariant = "prequal" | "combined";
export type AccountSubmissionEntry = "full" | "quick";

/**
 * Which form the user was on when the event fired. Differs from `entry` once an
 * admin escalates a quick submit to the full multi-step form, which is the only
 * way to tell those two admin paths apart.
 */
export type AccountSubmissionSurface = "full" | "quick";
type AccountSubmissionStepVia = "next" | "sidebar";

type AccountSubmissionFlowContext = {
	flowVariant: AccountSubmissionFlowVariant;
	entry: AccountSubmissionEntry;
	surface: AccountSubmissionSurface;
};

type AccountSubmissionStepCompletedPayload = AccountSubmissionFlowContext & {
	stepName: string;
	stepIndex: number;
	durationMs: number;
	via: AccountSubmissionStepVia;
	stepVisitCount: number;
};

type AccountSubmissionStepCompletedWithErrorsPayload = AccountSubmissionStepCompletedPayload & {
	errorFieldPaths: string[];
};

type AccountSubmissionFlowCompletedPayload = AccountSubmissionFlowContext & {
	accountId: SuretyAccountId;
	accountVariant: ContractSuretyAccountUnderwritingModelSize;
	totalDurationMs: number;
	isPartial: boolean;
	emptyFieldPaths: string[];
	emptyPartialFieldPaths: string[];
	financialFilesUploadedCount: number;
};

type AccountSubmissionFlowFailedPayload = AccountSubmissionFlowContext & {
	errorMessage: string;
};

type AccountSubmissionFlowAbandonedPayload = AccountSubmissionFlowContext & {
	lastStepName: string;
	lastStepIndex: number;
	furthestStepName: string;
	furthestStepIndex: number;
	totalDurationMs: number;
	emptyFieldPaths: string[];
};

export function trackAccountSubmissionFlowEntered(payload: AccountSubmissionFlowContext) {
	amplitude.track("Account Submission Flow Entered", payload);
}

export function trackAccountSubmissionStepCompleted(
	payload: AccountSubmissionStepCompletedPayload,
) {
	amplitude.track("Account Submission Step Completed", payload);
}

export function trackAccountSubmissionStepCompletedWithErrors(
	payload: AccountSubmissionStepCompletedWithErrorsPayload,
) {
	amplitude.track("Account Submission Step Completed With Errors", payload);
}

export function trackAccountSubmissionFlowCompleted(
	payload: AccountSubmissionFlowCompletedPayload,
) {
	amplitude.track("Account Submission Flow Completed", payload);
}

export function trackAccountSubmissionFlowFailed(payload: AccountSubmissionFlowFailedPayload) {
	amplitude.track("Account Submission Flow Failed", payload);
}

export function trackAccountSubmissionFlowAbandoned(
	payload: AccountSubmissionFlowAbandonedPayload,
) {
	amplitude.track("Account Submission Flow Abandoned", payload);
}

export type AttachmentAction =
	| "preview"
	| "view_summary"
	| "view_extraction"
	| "download"
	| "download_all"
	| "delete";
export type AttachmentContext =
	| "account-documents"
	| "bond-request-attachments"
	| "bond-attachments"
	| "indemnity"
	| "wip-statements"
	| "submission-upload";

export function trackAttachmentAction(action: AttachmentAction, context: AttachmentContext) {
	amplitude.track("Attachment Action", { action, context });
}

export type CommentsExportEntityType = "account" | "bondRequest" | "bond";

/**
 * The comments PDF is generated client-side and saved from a blob URL, so the
 * browser never emits a File Downloaded event for it.
 */
export function trackCommentsExported(entityType: CommentsExportEntityType, commentCount: number) {
	amplitude.track("Comments Exported", { entityType, commentCount });
}
